const post = {
  slug: "abandoned-carts-instagram-dms",
  title: "Abandoned Carts in Instagram DMs: How to Follow Up Without Being Pushy",
  seoTitle: "Abandoned Instagram Carts: How to Follow Up",
  description:
    "Customers add to cart in your DMs and go quiet. Learn why it happens, how to spot stuck conversations and how to follow up in a way that gets replies.",
  excerpt:
    "A customer adds to cart and then goes quiet. It is not always a no. Here is how to find out why, and how to follow up without becoming annoying.",
  category: "Selling on Instagram",
  tags: ["Abandoned cart", "Instagram DM follow up", "Cart recovery"],
  date: "2026-10-11",
  cover: {
    src: "/blog-images/abandoned-carts-instagram-dms-cover.png",
    alt: "A phone showing a gentle follow-up message about a product left in the cart",
  },
  blocks: [
    {
      type: "p",
      text:
        "In a DM checkout, a customer can add a product to their cart and then simply stop replying. In a physical shop, that is someone putting an item back and walking out.",
    },
    {
      type: "p",
      text:
        "The difference is that on Instagram you can still say something. The trick is to do it in a way that helps the customer instead of chasing them.",
    },

    { type: "h2", text: "Why people go quiet" },
    {
      type: "ul",
      items: [
        "**Life happened.** A call, a meeting, a notification. They meant to come back.",
        "**A question they did not ask.** Delivery time, return policy or whether the fabric is thick.",
        "**Doubt about size or fit.** They are not sure and do not want to commit.",
        "**Waiting for a discount.** Some people hesitate in the hope you will offer one.",
        "**The payment step felt like effort.** Anything that asks for too much work at the last step loses people.",
      ],
    },

    { type: "h2", text: "Find the stuck conversations" },
    {
      type: "p",
      text:
        "On every plan, your Chats tab shows each customer\u2019s cart, quantity and current stage. Starter and above also get an abandoned-cart alert in the Stuck tab.",
    },
    {
      type: "p",
      text:
        "If a customer told the bot why they hesitated, for example by asking for a discount, that reason is shown too. It appears in full on Growth and Pro, and for a limited number of carts on Starter.",
    },
    {
      type: "figure",
      src: "/blog-images/abandoned-carts-instagram-dms-figure.png",
      alt: "Timeline of an abandoned cart from cart added to follow up and paid order",
      caption: "What happens between \u201Cadded to cart\u201D and a paid order.",
      width: 1200,
      height: 1000,
    },

    { type: "h2", text: "Follow up the right way" },
    {
      type: "ol",
      items: [
        "**Respond to the reason, not the cart.** If they asked about sizing, answer sizing. Do not just say \u201Cyou left something behind.\u201D",
        "**Send one short message.** One line, one question. Three reminders in a row feel like pressure.",
        "**Make it easy to reply.** Ask something that can be answered in a word.",
        "**Mind the timing.** Instagram limits when a business can message a customer after their last message, so follow-ups work best when they are sent soon.",
        "**Offer help before a discount.** Reach for a discount only if price was actually the reason.",
      ],
    },
    {
      type: "chat",
      shop: "yourshop",
      title: "A gentle follow-up",
      caption: "Illustrative example of a helpful follow-up message.",
      messages: [
        { from: "shop", text: "Hi! Your Linen Kurta Set (M) is still in your cart. Anything we can help with, like sizing or delivery time?" },
        { from: "buyer", text: "Is it true to size?" },
        { from: "shop", text: "Yes, it fits true to size. If you are between sizes, we suggest going up one." },
        { from: "buyer", text: "Okay, going with M" },
      ],
    },
    {
      type: "callout",
      tone: "mint",
      title: "Automatic reminders",
      text:
        "On Growth and Pro, Checkout DM sends reminders automatically to customers who add to cart and go quiet, so you do not have to chase each one by hand.",
    },

    { type: "h2", text: "Reduce abandoned carts before they happen" },
    {
      type: "ul",
      items: [
        "**Keep stock accurate.** Nothing breaks trust like choosing a size and then hearing it is sold out.",
        "**Make the answers easy to find.** Delivery time and return policy should be one message away.",
        "**Keep payment simple.** Fewer steps at the end means fewer people lost. See [Taking Payments on Instagram: UPI vs a Payment Gateway](/blog/accept-payments-instagram-upi-vs-gateway).",
      ],
    },

    { type: "h2", text: "Match the message to the reason" },
    {
      type: "p",
      text:
        "A follow-up that fits the reason a customer stopped is far more likely to get a reply than a generic reminder. These examples show how the wording changes with the cause.",
    },
    {
      type: "table",
      headers: ["Why they stopped", "A helpful follow-up"],
      rows: [
        ["They got distracted", "Hi! Your Linen Kurta Set (M) is still in your cart. Would you like to finish your order?"],
        ["They were unsure about size", "Not sure about the size? Here is how it fits: true to size, and if you are between sizes we suggest going up one."],
        ["They had a delivery question", "Delivery usually takes a few days after dispatch. Want me to confirm the time for your address?"],
        ["They were waiting for a better price", "Thanks for your interest! If it helps, here is what is included in the set. Is there anything else you would like to know?"],
        ["Payment felt like effort", "Here is your payment link again. It takes under a minute, and you can pay by UPI, card or wallet."],
      ],
    },
    {
      type: "p",
      text:
        "Notice that none of these threaten, pressure or invent urgency. They answer the likely question and make the next step easy.",
    },

    { type: "h2", text: "Timing your follow-up" },
    {
      type: "p",
      text:
        "Timing matters more than most sellers expect. Instagram limits when a business can message a customer after their last message, so a follow-up that arrives too late may not be allowed at all, and one that arrives too early can feel pushy.",
    },
    {
      type: "ul",
      items: [
        "**Give them a little time.** People step away for a reason, and a message a few minutes after they stop is likely to interrupt.",
        "**Follow up within the allowed window.** Plan to send your message while Instagram still allows it, rather than days later.",
        "**Send once, then wait.** One thoughtful message beats three reminders.",
        "**Let automation help.** On Growth and Pro, reminders go out automatically, so the timing is handled for you.",
      ],
    },

    { type: "h2", text: "A weekly routine for stuck carts" },
    {
      type: "p",
      text:
        "If you handle follow-ups yourself, a short routine keeps them from piling up. Set aside ten minutes at the same time each day.",
    },
    {
      type: "ol",
      items: [
        "Open your Stuck tab, if your plan includes it, or your Chats tab, and look for carts that have gone quiet.",
        "Read the last few messages to find the reason the customer stopped.",
        "Send one short message that answers that reason.",
        "Note any question that keeps coming up, and add the answer to your catalog or your standard replies.",
        "At the end of the week, count how many carts you recovered and what worked.",
      ],
    },

    { type: "h2", text: "What not to do" },
    {
      type: "ul",
      items: [
        "**Do not pretend a product is almost sold out** when it is not. Customers notice, and trust is hard to rebuild.",
        "**Do not message customers repeatedly.** If someone does not reply to one follow-up, let them go.",
        "**Do not offer a discount to everyone by default.** It teaches customers to wait for one.",
        "**Do not ignore the pattern.** If many carts stall at the same step, fix that step.",
      ],
    },
    {
      type: "p",
      text:
        "Recovering carts is part of a wider flow. For the whole picture, from the first comment to the shipped order, read [How to Automate Sales on Instagram](/blog/how-to-automate-sales-on-instagram).",
    },
  ],
  related: [
    { to: "/abandoned-cart-guide", title: "Abandoned Cart Reminders Guide", description: "How Checkout DM follows up with customers who add to cart but go quiet." },
    { to: "/status-guide", title: "Status Badges Guide", description: "What every status badge means in your Chats and Orders tabs." },
    { to: "/tabs-guide", title: "Dashboard Tabs Guide", description: "Including the Chats and Stuck tabs." },
  ],
  faqs: [
    {
      q: "How do I see which customers abandoned their cart?",
      a: "On every plan, the Chats tab shows each customer's cart, quantity and stage. Starter and above also get an abandoned-cart alert in the Stuck tab.",
    },
    {
      q: "Does Checkout DM send cart reminders automatically?",
      a: "Yes, on Growth and Pro. Those plans automatically send reminders to customers who add to cart and go quiet.",
    },
    {
      q: "Can I message a customer any time after they stop replying?",
      a: "No. Instagram limits when a business can message a customer after their last message, so follow-ups should go out soon rather than days later.",
    },
  ],
};

export default post;
